import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "../redux-hooks";
import { SearchDocumentRequest } from "../../../components/operation/document/DocumentList";
import { checkExternalLinkAuthentication, deleteProdDocument, getDocumentCategories, getProductDocument, publishDocument, searchDocuments } from "../../../service/DocumentService";
import { InterceptorOption, LoadingType } from "../../../types/CommonTypes";
import { updateElementValue } from "../../../utils/updateElementValue";

export type SearchDocument = {
  agencyName: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  address: string | undefined;
  commType: string | undefined;
  agencyStatus: string | undefined;
  currentPage: number;
  itemsPerPage: number;
};

export type DocumentUpload = {
  documentType: string | undefined;
  effectiveDate: string | undefined;
  uploadDocument: string | undefined;
  documentName: string | undefined;
  documentURL: string | undefined;
  productTag: string | undefined;
  manufacturerTag: string | undefined;
  categoryTag: string | undefined;
  subCategoryTag: string | undefined;
  publishTag: string | undefined;
};

export type ProductDocumentListInfo = {
  entityId: string;
  productTemplateId: number;
  type: string;
  externalLinkType: string,
  documentType: string;
  entityName: string;
  documentName: string;
  description: string;
  comments: string | JSX.Element;
  documentURL: string | JSX.Element | null;
  productId: number;
  productName: string;
  status: string | JSX.Element
  manufacturer: any;
  manufacturerId: number;
  manufacturerName: string;
  publish: string;
  productDocumentInfo: {
    documentURL: string
  }
  documentId: number;
  productDocumentId: number;
  effectiveDate: string;
  category: string,
  subCategory: string
}

export type DocumentFile = {
  productDocumentId: number,
  documentId: number,
  documentName: string;
  documentType: string;
  externalLinkType: string,
  productDocumentInfo: { documentURL: string }
  dateUploaded: string;
  effectiveDate: string;
};

export type PublishDocumentRequest = {
  productDocumentId : number;
  publish: boolean
}

export type DocumentSubCategory = {
  subCategory: string;
  files: DocumentFile[];
};

export type DocumentCategory = {
  category: string;
  subCategories: DocumentSubCategory[];
};

type DocumentState = {
  status: string;
  error: string | null;
  searchDocumentList: {
            content: ProductDocumentListInfo[],
            totalElements: number,
            totalPages: number,
            size: number
  } | null;
  documentCategoryList: DocumentCategory[] | null;
  productDocumentById: ProductDocumentListInfo | null;
  searchDocumentListStatus: LoadingType;
  getDocumentCategoryListStatus: LoadingType;
  authenticateExternalLinkStatus: LoadingType;
  publishDocumentStatus: LoadingType;
  productDocumentIdStatus: LoadingType;
  deleteProductDocumentStatus: LoadingType;
}

const initialState: DocumentState = {
  status: "idle",
  searchDocumentList: null,
  searchDocumentListStatus: 'idle',
  documentCategoryList: null,
  productDocumentById: null,
  getDocumentCategoryListStatus: 'idle',
  authenticateExternalLinkStatus: 'idle',
  publishDocumentStatus: 'idle',
  productDocumentIdStatus: 'idle',
  deleteProductDocumentStatus: 'idle',
  error: null

};

// export const productCategories = new Map([
//   ["Practice Onboarding", [
//     "Practice Registration",
//     "Practice Detail Entry",
//     "Practice Onboarding Call"
//   ]],
//   ["Onboarding Details + Info", [
//     "Onboarding SOP",
//     "Protect Account"
//   ]],
//   ["Catalogs and Grids", [
//     "Catalogs and Product Grids",
//     "Conservative Collagen Program",
//     "Post Operation Program"
//   ]],
//   ["V3 Marketing Collateral", [
//     "One-Pager and Tri Fold"
//   ]],
//   ["Manufacturer SOPs", [
//     "Manufacturer SOPs"
//   ]],
//   ["Platform Training", [
//     "Platform Overview",
//     "Managing Notifications in the Platform",
//     "Preview to Patient List",
//     "Login to the Demo Platform"
//   ]],
//   ["Login to the Platform", [
//     "v3biomed.com"
//   ]],
//   ["Product Catalog", [
//     "Product Portfolio"
//   ]],
//   ["Agent Registration", [
//     "Adding a new sub-rep"
//   ]],
//   ["Meeting Links", [
//     "Meeting w/ Josh Link(VP of Sales)",
//     "Rep Software Training Link"
//   ]],
//   ["Assurance", [
//     "Assurance Programs"
//   ]],
//   ["Clinical and Compliance", [
//     "Graft Audit Checklist",
//     "Wound Assessment Guide",
//     "Kari Harman Clinical Wound Care Rep Training Video"
//   ]],
//   ["Collagen", [
//     "Healing Biologics",
//     "Collagen Study"
//   ]],
//   ["Active CTPs", [
//     "RedDress-ActiGraft",
//     "Derm-Maxx",
//     "Membrane Wrap",
//     "Wound Fix",
//     "WoundPlus Membrane",
//     "Complete AA",
//     "Complete ACA",
//     "Restorigin",
//     "Complete FT",
//     "Barrera",
//     "CarePatch",
//     "Amnio TriCore",
//     "Amnio QuadCore",
//     "AmnioCore Pro",
//     "AmnioCore Pro Plus",
//     "AmnioCore",
//     "Impax",
//     "Orion",
//     "Biovance",
//     "Biovance 3L",
//     "Amnio AMP",
//     "Amnio-Maxx",
//     "Esano ACA",
//     "Xcell Amnio",
//     "CarePatch OLD",
//     "Dermacyte",
//     "AmnioBand (OLD)",
//     "Rebound Matrix",
//     "Zenith",
//     "SurGraft FT",
//     "Coll-e-derm",
//     "AmnioCore (OLD)",
//     "Procenta",
//     "AmnioBand",
//     "Wound Plus",
//     "ActiGraft",
//     "MariGen",
//     "RevoShield+",
//     "MLG Woundcare",
//     "Complete SL",
//     "Marigen Expanse",
//     "Shield Standard",
//     "CaregraFT",
//     "ACApatch",
//     "Amnio- Maxx",
//     "Amnio AMP-MP",
//     "Activate",
//     "Dermabind FM | Altera",
//     "Derm-Maxx -",
//     "Biovance-",
//     "Membrane Wrap Hydro-"
//   ]]
// ]);

export const fetchDocumentList  = createAsyncThunk("searchDocumentList", async (searchDocumentRequest: SearchDocumentRequest) => {
  const response = await searchDocuments(searchDocumentRequest);
  const v3Response = response.data;
  return v3Response.data;
});

export const getDocumentCategoryList = createAsyncThunk("getDocumentCategoryList", async (onlyPublished: boolean) => {
  const response = await getDocumentCategories(onlyPublished);
  const v3Response = response.data;
  return v3Response.data;
});

export const authenticateExternalLink = createAsyncThunk<any, {password: string, interceptorOption: InterceptorOption}>(
  "authenticateExternalLink", 
  async (data, { rejectWithValue} ) => {
    const { password, interceptorOption } = data;
  try {
    const response = await checkExternalLinkAuthentication(password, interceptorOption);
    const v3Response = response.data;
    return v3Response.data;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});

export const publishProductDocument = createAsyncThunk("publishProductDocument", async (publishDocumentRequest: PublishDocumentRequest) => {
  const response = await publishDocument(publishDocumentRequest);
  const v3Response = response.data;
  return v3Response.data;
});

export const getProductDocumentById = createAsyncThunk("getProductDocumentById", async (productDocumentId: number) => {
  const response = await getProductDocument(productDocumentId);
  const v3Response = response.data;
  return v3Response.data;
});

export const deleteProductDocument = createAsyncThunk("deleteProductDocument", async (productDocumentId: number) => {
  const response = await deleteProdDocument(productDocumentId);
  const v3Response = response.data;
  return v3Response.data;
});

const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    updateElementInDocumentSlice : updateElementValue,
    resetManufacturerData: (state: DocumentState) => {
      return {
        ...state,
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentList.pending, (state) => {
        state.searchDocumentListStatus = "loading";
      })
      .addCase(fetchDocumentList.fulfilled, (state, action ) => {
        state.searchDocumentListStatus = "success";
        state.searchDocumentList = action.payload
      })
      .addCase(fetchDocumentList.rejected, (state, action) => {
        state.searchDocumentListStatus = 'error';
      })
      .addCase(getDocumentCategoryList.pending, (state) => {
        state.getDocumentCategoryListStatus = "loading";
      })
      .addCase(getDocumentCategoryList.fulfilled, (state, action ) => {
        state.getDocumentCategoryListStatus = "success";
        state.documentCategoryList = action.payload
      })
      .addCase(getDocumentCategoryList.rejected, (state, action) => {
        state.getDocumentCategoryListStatus = 'error';
      })
      .addCase(authenticateExternalLink.pending, (state) => {
        state.authenticateExternalLinkStatus = "loading";
      })
      .addCase(authenticateExternalLink.fulfilled, (state, action ) => {
        state.authenticateExternalLinkStatus = "success";
      })
      .addCase(authenticateExternalLink.rejected, (state, action) => {
        state.authenticateExternalLinkStatus = 'error';
      })
      .addCase(publishProductDocument.pending, (state) => {
        state.publishDocumentStatus = "loading";
      })
      .addCase(publishProductDocument.fulfilled, (state, action ) => {
        state.publishDocumentStatus = "success";
      })
      .addCase(publishProductDocument.rejected, (state, action) => {
        state.publishDocumentStatus = 'error';
      })
      .addCase(getProductDocumentById.pending, (state) => {
        state.productDocumentIdStatus = "loading";
      })
      .addCase(getProductDocumentById.fulfilled, (state, action ) => {
        state.productDocumentIdStatus = "success";
        state.productDocumentById = action.payload;
      })
      .addCase(getProductDocumentById.rejected, (state, action) => {
        state.productDocumentIdStatus = 'error';
      })
      .addCase(deleteProductDocument.pending, (state) => {
        state.deleteProductDocumentStatus = "loading";
      })
      .addCase(deleteProductDocument.fulfilled, (state, action ) => {
        state.deleteProductDocumentStatus = "success";
      })
      .addCase(deleteProductDocument.rejected, (state, action) => {
        state.deleteProductDocumentStatus = 'error';
      })
  }
});
export const { updateElementInDocumentSlice } = documentSlice.actions;
export const useDocumentReducer = () => useAppSelector((state) => state.documentSlice);
export default documentSlice.reducer;